/* HR — Roles & Permissions (R7+ fully wired)
 *
 *  Authorisation:
 *    Tab 1 (Roles):  admin only for create/edit/delete; 任何登入者可看
 *    Tab 2 (Users):  hr/admin can view; admin can grant/revoke
 *
 *  Endpoints:
 *    GET    /api/roles?active_only=
 *    POST   /api/roles                                       (admin)
 *    PATCH  /api/roles/:id                                   (admin; code/is_system 不可改)
 *    DELETE /api/roles/:id                                   (admin; is_system 與 in-use 不可刪)
 *    GET    /api/users/{user_id}/roles                       (hr/admin)
 *    POST   /api/users/{user_id}/roles    { role_code }      (admin)
 *    DELETE /api/users/{user_id}/roles/{role_code}           (admin)
 *    GET    /api/admin/users                                 (admin; 用來列出全部 users 給 RBAC UI)
 */

/* ─────────────────────────  Role form  ──────────────────── */

function RoleForm({ initial, onCancel, onSaved }) {
  const toast = useToast();
  const isEdit = !!initial?.id;
  const [code, setCode] = React.useState(initial?.code || '');
  const [name, setName] = React.useState(initial?.name || '');
  const [nameEn, setNameEn] = React.useState(initial?.name_en || '');
  const [description, setDescription] = React.useState(initial?.description || '');
  const [sortOrder, setSortOrder] = React.useState(initial?.sort_order ?? 0);
  const [active, setActive] = React.useState(initial?.active ?? true);
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async () => {
    if (!code.trim() || !name.trim()) { toast('代碼與名稱必填', 'error'); return; }
    setSubmitting(true);
    try {
      if (isEdit) {
        // code 不可改 (後端拒絕)
        await api('PATCH', `/roles/${initial.id}`, {
          name: name.trim(), name_en: nameEn.trim() || null,
          description: description.trim() || null,
          sort_order: parseInt(sortOrder) || 0,
          active,
        });
      } else {
        await api('POST', '/roles', {
          code: code.trim(), name: name.trim(),
          name_en: nameEn.trim() || null,
          description: description.trim() || null,
          sort_order: parseInt(sortOrder) || 0,
          active,
        });
      }
      toast(isEdit ? '已更新' : '已新增角色', 'ok');
      onSaved();
    } catch (e) { toast((isEdit ? '更新' : '新增') + '失敗：' + (e.detail || ''), 'error'); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      open onClose={onCancel}
      title={isEdit ? `編輯角色 — ${initial.name}` : '新增角色'}
      subtitle="Role"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>取消</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>{submitting ? '處理中…' : (isEdit ? '儲存' : '新增')}</Button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="代碼" required hint={isEdit ? '建立後不可修改' : 'snake_case，例：finance / manager'}>
          <TextInput value={code} onChange={setCode} mono disabled={isEdit} />
        </Field>
        <Field label="排序">
          <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} style={inputStyle({ fontFamily: HR.mono, textAlign: 'right' })} />
        </Field>
        <Field label="顯示名稱（中）" required>
          <TextInput value={name} onChange={setName} />
        </Field>
        <Field label="顯示名稱（英）">
          <TextInput value={nameEn} onChange={setNameEn} />
        </Field>
        <Field label="描述" span={2}>
          <Textarea value={description} onChange={setDescription} rows={3} />
        </Field>
        <Field label="啟用" span={2}>
          <select value={active ? '1' : '0'} onChange={(e) => setActive(e.target.value === '1')} style={inputStyle()}>
            <option value="1">啟用中</option>
            <option value="0">已停用</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
}

/* ─────────────────────────  Roles tab  ──────────────────── */

function RolesTab({ user }) {
  const toast = useToast();
  // 角色 CRUD 對應 capability：rbac.role.manage（admin only per role matrix）
  const canManage = hasCapability(user, 'rbac.role.manage');
  const [roles, setRoles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [editing, setEditing] = React.useState(null);
  const [confirmDelete, setConfirmDelete] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const list = await api('GET', '/roles?active_only=false').catch(() => []);
    setRoles(Array.isArray(list) ? list : []);
    setLoading(false);
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api('DELETE', `/roles/${confirmDelete.id}`);
      toast('已刪除', 'ok');
      setConfirmDelete(null);
      refresh();
    } catch (e) { toast('刪除失敗：' + (e.detail || ''), 'error'); }
    finally { setDeleting(false); }
  };

  return (
    <>
      <Card padding="0" eyebrow="Roles" title="系統角色" italic={`${roles.length} 個角色`} right={
        canManage && <Button variant="primary" size="md" icon="plus" onClick={() => setEditing({})}>新增角色</Button>
      }>
        {loading ? (
          <SkeletonTable rows={6} />
        ) : roles.length === 0 ? (
          <EmptyState icon="settings" title="尚未建立任何角色" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left',  fontWeight: 500 }}>代碼</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>名稱</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>英文名</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>描述</th>
                <th style={{ padding: '11px 16px',          textAlign: 'right', fontWeight: 500 }}>使用者</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>狀態</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {roles.map(r => (
                <tr key={r.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                  <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs, fontWeight: 500 }}>
                    {r.code}
                    {r.is_system && (
                      <span style={uiStyle({
                        marginLeft: 6, padding: '1px 5px', borderRadius: 2,
                        background: '#553C7B', color: '#fff', fontSize: FontSizes.trading_xxs, fontWeight: 700,
                      }, {
                        padding: '2px 6px', borderRadius: GxT.radius.chipSm,
                        background: GxCore.tone('done').bg, color: GxCore.tone('done').fg,
                      })}>SYS</span>
                    )}
                  </td>
                  <td style={{ padding: '13px 16px', fontWeight: 500 }}>{r.name}</td>
                  <td style={{ padding: '13px 16px', fontFamily: HR.serif, fontStyle: uiPick('italic', 'normal'), color: HR.inkSoft }}>{r.name_en || '—'}</td>
                  <td style={{ padding: '13px 16px', color: HR.inkSoft, fontSize: FontSizes.xxs }}>{r.description || '—'}</td>
                  <td style={{ padding: '13px 16px', textAlign: 'right', fontFamily: HR.mono, fontWeight: 600 }}>{r.user_count}</td>
                  <td style={{ padding: '13px 16px' }}>
                    {r.active ? <Pill tone="accent" dot>啟用中</Pill> : <Pill tone="neutral">已停用</Pill>}
                  </td>
                  <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                    {canManage && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>編輯</Button>
                        {!r.is_system && r.user_count === 0 && (
                          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)} style={{ color: HR.down }}>刪除</Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {editing !== null && (
        <RoleForm
          initial={editing.id ? editing : null}
          onCancel={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      <ConfirmDialog
        open={!!confirmDelete}
        title="刪除角色"
        message={confirmDelete ? `確定刪除角色「${confirmDelete.name}」？此操作無法復原。系統預設角色與已在使用中的角色無法刪除。` : ''}
        danger busy={deleting}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={doDelete}
      />
    </>
  );
}

/* ─────────────────────────  User-roles editor modal  ────── */

function UserRolesEditor({ targetUser, roles, onClose, onChanged, canEdit }) {
  const toast = useToast();
  const [userRoles, setUserRoles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(null);  // role_code being processed
  const [grantCode, setGrantCode] = React.useState('');

  const refresh = React.useCallback(async () => {
    setLoading(true);
    const list = await api('GET', `/users/${targetUser.id}/roles`).catch(() => []);
    setUserRoles(Array.isArray(list) ? list : []);
    setLoading(false);
  }, [targetUser.id]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const grant = async () => {
    if (!grantCode) return;
    setBusy(grantCode);
    try {
      await api('POST', `/users/${targetUser.id}/roles`, { role_code: grantCode });
      toast(`已授予 ${targetUser.username} → ${grantCode}`, 'ok');
      setGrantCode('');
      await refresh();
      onChanged();
    } catch (e) { toast('授予失敗：' + (e.detail || ''), 'error'); }
    finally { setBusy(null); }
  };

  const revoke = async (roleCode) => {
    if (!confirm(`確定撤銷 ${targetUser.username} 的「${roleCode}」角色？`)) return;
    setBusy(roleCode);
    try {
      await api('DELETE', `/users/${targetUser.id}/roles/${encodeURIComponent(roleCode)}`);
      toast(`已撤銷 ${roleCode}`, 'ok');
      await refresh();
      onChanged();
    } catch (e) { toast('撤銷失敗：' + (e.detail || ''), 'error'); }
    finally { setBusy(null); }
  };

  const userRoleCodes = new Set(userRoles.map(r => r.role_code));
  const grantableRoles = roles.filter(r => r.active && !userRoleCodes.has(r.code));

  return (
    <Modal
      open onClose={onClose}
      title={`角色設定 — ${targetUser.display_name || targetUser.username}`}
      subtitle={`@${targetUser.username} · #${targetUser.id}`}
      size="md"
      footer={<Button variant="ghost" onClick={onClose}>關閉</Button>}
    >
      {loading ? (
        <SkeletonText lines={4} />
      ) : (
        <>
          <div style={{ marginBottom: 18 }}>
            <div style={uiStyle({
              fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: HR.inkFaint, marginBottom: 10,
            }, { fontSize: GxT.size.label, letterSpacing: 0, textTransform: 'none', fontWeight: GxT.weight.label, color: GxT.color.onV })}>當前角色（{userRoles.length}）</div>
            {userRoles.length === 0 ? (
              <div style={{ fontSize: FontSizes.xs, color: HR.inkFaint, fontStyle: uiPick('italic', 'normal'), padding: '10px 0' }}>
                此使用者沒有任何角色 — 至少授予一個角色才能使用 Workspace 功能。
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {userRoles.map(ur => (
                  <div key={ur.role_code} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 14px', background: HR.surfaceAlt, borderRadius: uiPick(4, GxT.radius.inner),
                  }}>
                    <span style={{ fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft, minWidth: 100 }}>{ur.role_code}</span>
                    <span style={{ fontWeight: 500 }}>{ur.role_name}</span>
                    <span style={{ fontSize: FontSizes.trading_lg, color: HR.inkFaint }}>
                      授予於 {fmtDateTime(ur.granted_at)}
                      {ur.granted_by_username && ` · 由 ${ur.granted_by_username}`}
                    </span>
                    <span style={{ flex: 1 }} />
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => revoke(ur.role_code)} disabled={busy === ur.role_code} style={{ color: HR.down }}>
                        {busy === ur.role_code ? '處理中…' : '撤銷'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {canEdit && grantableRoles.length > 0 && (
            <div>
              <div style={uiStyle({
                fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase',
                color: HR.inkFaint, marginBottom: 10,
              }, { fontSize: GxT.size.label, letterSpacing: 0, textTransform: 'none', fontWeight: GxT.weight.label, color: GxT.color.onV })}>授予新角色</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={grantCode} onChange={(e) => setGrantCode(e.target.value)} style={inputStyle()}>
                  <option value="">— 選擇角色 —</option>
                  {grantableRoles.map(r => (
                    <option key={r.code} value={r.code}>{r.name} ({r.code})</option>
                  ))}
                </select>
                <Button variant="primary" onClick={grant} disabled={!grantCode || busy === grantCode}>
                  {busy === grantCode ? '授予中…' : '授予'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

/* ─────────────────────────  Users tab  ──────────────────── */

function UsersTab({ user }) {
  // rbac.user.assign → admin only (per matrix); HR 沒這個 capability，但能用 rbac 看
  // 此分頁顯示權限：擁有 rbac.user.assign 才能 grant/revoke；否則只能瀏覽
  const canAssign = hasCapability(user, 'rbac.user.assign');
  // 「能看 /admin/users」對應 admin only（後端 require_admin）— hr/admin 走 /users/:id/roles 看單一
  // 為避免 hr 點進來打 500/403，我們檢查是否有 rbac.user.assign 或 admin 角色
  const canViewUserList = canAssign;  // 目前 capability 矩陣只 admin 有
  const [users, setUsers] = React.useState([]);
  const [roles, setRoles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [resetTarget, setResetTarget] = React.useState(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    // /admin/users 回 PaginatedResponse — 抓最多 200 筆給 RBAC 介面用（內部使用者數遠 < 200）
    const [u, r] = await Promise.all([
      api('GET', '/admin/users?page_size=200').catch(() => ({ items: [] })),
      api('GET', '/roles?active_only=true').catch(() => []),
    ]);
    setUsers(Array.isArray(u?.items) ? u.items : []);
    setRoles(Array.isArray(r) ? r : []);
    setLoading(false);
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);

  // SSE：別人改了 users / roles，即時 refetch
  const userEvent = useServerEvent('users.changed');
  React.useEffect(() => { if (userEvent) refresh(); }, [userEvent, refresh]);

  // 非員工帳號（analyst / analyst_lite / non_staff）由專屬「非員工帳號」分頁管理，這裡不列，也不計入總數。
  const manageable = users.filter(u => !(u.roles || []).some(r => NON_STAFF_ROLE_CODES.includes(r)));

  const filtered = manageable.filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      u.username?.toLowerCase().includes(q) ||
      u.display_name?.toLowerCase().includes(q) ||
      u.discord_name?.toLowerCase().includes(q) ||
      String(u.id).includes(q)
    );
  });

  // Role code → human-readable name lookup. Falls back to code when the role
  // is no longer in the catalog (e.g., user still tagged with a deleted role).
  const roleNameByCode = React.useMemo(
    () => Object.fromEntries(roles.map(r => [r.code, r.name])),
    [roles]
  );

  if (!canViewUserList) {
    return (
      <EmptyState icon="search" title="無權檢視" hint="此分頁需要 rbac.user.assign capability（目前僅 admin）。" />
    );
  }

  return (
    <>
      <Card padding="0" eyebrow="User Roles" title="使用者角色設定" italic={`${filtered.length} / ${manageable.length} 位`}>
        <div style={{ padding: '12px 24px', borderBottom: `1px solid ${HR.rule}` }}>
          <TextInput value={search} onChange={setSearch} placeholder="搜尋 username / 顯示名 / Discord 名 / id…" />
        </div>
        {loading ? (
          <SkeletonTable rows={6} />
        ) : filtered.length === 0 ? (
          <EmptyState icon="search" title={search ? '無符合條件的使用者' : '尚未有任何使用者'} />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left',  fontWeight: 500, width: 60 }}>ID</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>Username</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>顯示名</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>角色</th>
                <th style={{ padding: '11px 16px',          textAlign: 'left',  fontWeight: 500 }}>狀態</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                  <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>#{u.id}</td>
                  <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontWeight: 500 }}>{u.username}</td>
                  <td style={{ padding: '13px 16px', fontFamily: HR.serif }}>{u.display_name || '—'}</td>
                  <td style={{ padding: '13px 16px' }}>
                    {(u.roles || []).length === 0 ? (
                      <span style={{ color: HR.inkFaint, fontSize: FontSizes.xxs, fontStyle: uiPick('italic', 'normal') }}>（無角色）</span>
                    ) : (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(u.roles || []).map(rc => (
                          <span key={rc} title={rc} style={uiStyle({
                            padding: '2px 7px', borderRadius: 10, fontSize: FontSizes.trading_lg, fontWeight: 500,
                            background: HR.accentSoft, color: HR.accent,
                          }, { padding: '3px 8px', borderRadius: GxT.radius.chip, fontWeight: GxT.weight.label })}>{roleNameByCode[rc] || rc}</span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '13px 16px' }}>
                    {u.status === 'approved' ? <Pill tone="accent" dot>已核准</Pill>
                      : u.status === 'pending' ? <Pill tone="warn" dot>待核准</Pill>
                      : <Pill tone="neutral">{u.status || '—'}</Pill>}
                  </td>
                  <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      {canAssign && (
                        <Button size="sm" variant="ghost" onClick={() => setResetTarget(u)}
                          title="產生隨機新密碼並寄到該用戶 email">
                          重設密碼
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setSelectedUser(u)}>
                        {canAssign ? '管理角色' : '檢視'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {selectedUser && (
        <UserRolesEditor
          targetUser={selectedUser}
          roles={roles}
          canEdit={canAssign}
          onClose={() => setSelectedUser(null)}
          onChanged={refresh}
        />
      )}
      {resetTarget && (
        <ResetPasswordConfirm
          targetUser={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}
    </>
  );
}

function ResetPasswordConfirm({ targetUser, onClose }) {
  const toast = useToast();
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const resp = await api('POST', `/admin/users/${targetUser.id}/reset-and-email`);
      toast(resp.message || '已寄出新密碼', resp.email_sent ? 'ok' : 'warn');
      onClose();
    } catch (e) {
      toast(e.detail || '重設失敗', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open onClose={onClose}
      title="重設密碼"
      subtitle="Reset & Email"
      size="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>取消</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>
          {busy ? '寄送中…' : '確認重設並寄信'}
        </Button>
      </>}
    >
      <p style={{ margin: '0 0 12px', fontSize: FontSizes.trading_md, lineHeight: 1.6 }}>
        將為以下使用者產生**隨機 12 字新密碼**並直接寄到 email：
      </p>
      <div style={{
        background: HR.surfaceAlt, padding: '12px 14px', borderRadius: uiPick(4, GxT.radius.inner),
        fontSize: FontSizes.xs, fontFamily: HR.mono,
      }}>
        <div><strong>帳號：</strong>{targetUser.username}</div>
        {targetUser.display_name && <div style={{ marginTop: 4 }}><strong>顯示名：</strong>{targetUser.display_name}</div>}
        <div style={{ marginTop: 4, color: HR.inkSoft }}><strong>寄信至：</strong>{targetUser.username}</div>
      </div>
      <p style={{ margin: '14px 0 0', fontSize: FontSizes.xxs, color: HR.inkFaint, lineHeight: 1.5 }}>
        ⚠ 舊密碼會立即失效。使用者收到信後登入即會生效，建議登入後到「修改密碼」改成自己好記的字串。
      </p>
    </Modal>
  );
}

/* ──────────────────  非員工帳號 tab  ────────────────────
 * 非員工帳號（analyst / non_staff 角色）的開帳介面。不走員工頁（員工頁會強制建
 * employees 記錄），改在這裡呼叫 POST /auth/register { account_type: 'analyst' | 'non_staff' }。
 * 後端 create_user(role='analyst'/'non_staff') 會只授對應角色（不帶 employee），
 * 完全不建 employees 記錄 → 自然不進出勤/薪資/請假母體。
 * 角色可疊加：若帳號兼具其他角色（如 admin+non_staff），「使用者角色」分頁會過濾掉它，
 * 改由本 tab 的「角色」按鈕（UserRolesEditor）管理，避免混搭帳號的角色卡住無法調整。 */

// account_type → UI 顯示文案（列表「類型」欄與建立 modal 共用）
const NON_STAFF_TYPE_LABELS = { analyst: '外部分析', analyst_lite: '一般外部分析', non_staff: '非員工' };
// 非員工帳號角色 code（analyst 家族 + non_staff）— 由「非員工帳號」分頁管理，不列入「使用者角色」分頁。
const NON_STAFF_ROLE_CODES = Object.keys(NON_STAFF_TYPE_LABELS);

function CreateAnalystModal({ onClose, onCreated }) {
  const toast = useToast();
  // 預設不選 — 強迫管理者明確選擇類型，避免肌肉記憶把外部分析師誤開成 non_staff
  const [accountType, setAccountType] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null); // { username, temp_password, email_sent }

  const submit = async () => {
    const u = username.trim();
    if (!accountType) { toast('請先選擇帳號類型', 'error'); return; }
    if (!u) { toast('請填寫帳號（email）', 'error'); return; }
    if (password && password.length < 8) { toast('密碼長度至少 8 個字元', 'error'); return; }
    setBusy(true);
    try {
      const body = { username: u, account_type: accountType };
      if (password) body.password = password;
      const resp = await api('POST', '/auth/register', body);
      setResult({
        username: resp.username || u,
        temp_password: resp.temp_password || (password ? null : '(未產生)'),
        email_sent: !!resp.email_sent,
        had_password: !!password,
        account_type: accountType,
      });
      toast(`${NON_STAFF_TYPE_LABELS[accountType]}帳號已建立`, 'ok');
      onCreated && onCreated();
    } catch (e) {
      toast('建立失敗：' + (e.detail || ''), 'error');
    } finally {
      setBusy(false);
    }
  };

  // 建立成功後改顯示結果（含臨時密碼）
  if (result) {
    return (
      <Modal open onClose={onClose} title={`${NON_STAFF_TYPE_LABELS[result.account_type]}帳號已建立`}
        subtitle={result.account_type === 'non_staff' ? 'Non-staff Account' : 'Analyst Account'} size="sm"
        footer={<Button variant="primary" onClick={onClose}>完成</Button>}>
        <div style={{ background: HR.surfaceAlt, padding: '12px 14px', borderRadius: uiPick(4, GxT.radius.inner), fontSize: FontSizes.xs, fontFamily: HR.mono }}>
          <div><strong>帳號：</strong>{result.username}</div>
          {!result.had_password && (
            <div style={{ marginTop: 6 }}>
              <strong>臨時密碼：</strong>
              <span style={{ color: HR.accent, fontWeight: 600 }}>{result.temp_password}</span>
            </div>
          )}
        </div>
        <p style={{ margin: '14px 0 0', fontSize: FontSizes.xxs, color: HR.inkFaint, lineHeight: 1.5 }}>
          {result.had_password
            ? '已用您指定的密碼建立。請另外告知對方帳密。'
            : (result.email_sent
                ? '臨時密碼已寄到該 email。'
                : '⚠ 未寄出 email（未設定郵件服務）— 請手動把上方臨時密碼交給對方。')}
          {' '}
          {result.account_type === 'non_staff'
            ? '該帳號可登入使用午餐等一般功能，不會出現在出勤/薪資。'
            : result.account_type === 'analyst_lite'
              ? '該帳號登入後只會看到「分析工具」整區，且不可下載報告。'
              : '該帳號登入後只會看到「分析工具」整區（可下載報告）。'}
        </p>
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title="新增非員工帳號" subtitle="Non-staff Account" size="sm"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>取消</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? '建立中…' : '建立帳號'}</Button>
      </>}>
      <div style={{ display: 'grid', gap: 14 }}>
        <Field label="帳號類型" required hint="決定該帳號能使用的功能範圍">
          <select value={accountType} onChange={(e) => setAccountType(e.target.value)} style={inputStyle()}>
            <option value="">— 選擇帳號類型 —</option>
            <option value="non_staff">一般非員工 non_staff</option>
            <option value="analyst">外部分析 analyst（可下載報告）</option>
            <option value="analyst_lite">一般外部分析 analyst_lite（不可下載報告）</option>
          </select>
        </Field>
        <Field label="帳號（email）" required hint="作為登入帳號，建議用對方 email">
          <TextInput value={username} onChange={setUsername} placeholder="someone@example.com" mono />
        </Field>
        <Field label="密碼" hint="留空 → 自動產生 12 字臨時密碼（若已設定郵件服務會寄出）">
          <TextInput value={password} onChange={setPassword} placeholder="（留空自動產生）" type="password" />
        </Field>
      </div>
      <p style={{ margin: '14px 0 0', fontSize: FontSizes.xxs, color: HR.inkFaint, lineHeight: 1.5 }}>
        {!accountType ? (
          <>請先選擇帳號類型 — 「外部分析」只能用分析工具，「一般非員工」只能用午餐等一般功能。</>
        ) : accountType === 'analyst' ? (
          <>此帳號為<strong>非員工</strong>身分，僅能使用分析工具（台股掃描器 / Deep Research / 文件知識庫 / 研究報告庫），
          無法存取交易、ERP、HR 等任何其他功能；<strong>可下載</strong>研究報告庫 / 早晨報告的報告檔。</>
        ) : accountType === 'analyst_lite' ? (
          <>此帳號為<strong>非員工</strong>身分，僅能使用分析工具（台股掃描器 / Deep Research / 文件知識庫 / 研究報告庫），
          無法存取交易、ERP、HR 等任何其他功能；且<strong>不可下載</strong>報告（僅能線上閱讀）。</>
        ) : (
          <>此帳號為<strong>非員工</strong>身分，可登入、使用午餐等一般功能，
          不建員工檔、不會出現在出勤/薪資。</>
        )}
      </p>
    </Modal>
  );
}

function AnalystAccountsTab({ user }) {
  const toast = useToast();
  const canManage = hasCapability(user, 'rbac.user.assign') || isAdmin(user);
  // 角色 grant/revoke 比照 UsersTab：擁有 rbac.user.assign（目前僅 admin）才能編輯
  const canAssign = hasCapability(user, 'rbac.user.assign');
  const [users, setUsers] = React.useState([]);
  const [roles, setRoles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [resetTarget, setResetTarget] = React.useState(null);
  // 「角色」按鈕 → UserRolesEditor（混搭帳號在此調整角色）
  const [rolesTarget, setRolesTarget] = React.useState(null);
  // 註銷／恢復 確認對話：{ user, action: 'disable' | 'enable' }
  const [statusTarget, setStatusTarget] = React.useState(null);
  const [statusBusy, setStatusBusy] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    // 角色主檔比照 UsersTab 一起抓，供 UserRolesEditor 的「授予新角色」下拉使用
    const [u, r] = await Promise.all([
      api('GET', '/admin/users?page_size=200').catch(() => ({ items: [] })),
      api('GET', '/roles?active_only=true').catch(() => []),
    ]);
    const items = Array.isArray(u?.items) ? u.items : [];
    // 非員工帳號 = roles 含 analyst（外部分析）/ analyst_lite（一般外部分析）/ non_staff（一般非員工）
    setUsers(items.filter(x => (x.roles || []).some(r => NON_STAFF_ROLE_CODES.includes(r))));
    setRoles(Array.isArray(r) ? r : []);
    setLoading(false);
  }, []);
  React.useEffect(() => { refresh(); }, [refresh]);

  const userEvent = useServerEvent('users.changed');
  React.useEffect(() => { if (userEvent) refresh(); }, [userEvent, refresh]);

  const doSetStatus = async () => {
    if (!statusTarget) return;
    const { user: target, action } = statusTarget;
    const status = action === 'disable' ? 'disabled' : 'approved';
    setStatusBusy(true);
    try {
      const resp = await api('PATCH', `/admin/users/${target.id}/status`, { status });
      toast(resp.message || (action === 'disable' ? '已註銷帳號' : '已恢復帳號'), 'ok');
      setStatusTarget(null);
      await refresh();
    } catch (e) {
      toast((action === 'disable' ? '註銷' : '恢復') + '失敗：' + (e.detail || ''), 'error');
    } finally {
      setStatusBusy(false);
    }
  };

  if (!canManage) {
    return <EmptyState icon="search" title="無權管理" hint="此分頁需要 admin 權限。" />;
  }

  return (
    <>
      <Card padding="0" eyebrow="Non-staff Accounts" title="非員工帳號" italic={`${users.length} 個帳號`} right={
        <Button variant="primary" size="md" icon="plus" onClick={() => setCreating(true)}>新增非員工帳號</Button>
      }>
        {loading ? (
          <SkeletonTable rows={4} />
        ) : users.length === 0 ? (
          <EmptyState icon="user" title="尚無非員工帳號" hint="點右上角「新增非員工帳號」建立第一個非員工帳號（外部分析或一般非員工）。" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FontSizes.xs }}>
            <thead>
              <tr style={{ background: HR.surfaceAlt, fontSize: FontSizes.trading_md, letterSpacing: '0.1em', textTransform: 'uppercase', color: HR.inkFaint }}>
                <th style={{ padding: '11px 16px 11px 24px', textAlign: 'left', fontWeight: 500, width: 60 }}>ID</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>帳號</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>類型</th>
                <th style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 500 }}>狀態</th>
                <th style={{ padding: '11px 24px 11px 16px', textAlign: 'right', fontWeight: 500 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                // 非員工角色以外的殘留角色（如 admin、hr）→ 混搭帳號，顯示警告
                const extraRoles = (u.roles || []).filter(r => !NON_STAFF_ROLE_CODES.includes(r));
                return (
                <tr key={u.id} style={{ borderTop: `1px solid ${HR.ruleSoft}` }}>
                  <td style={{ padding: '13px 16px 13px 24px', fontFamily: HR.mono, fontSize: FontSizes.xxs, color: HR.inkSoft }}>#{u.id}</td>
                  <td style={{ padding: '13px 16px', fontFamily: HR.mono, fontWeight: 500 }}>{u.username}</td>
                  <td style={{ padding: '13px 16px' }}>
                    <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                      {/* 每個持有的非員工角色各一顆 Pill（analyst+non_staff 混搭 = 兩顆） */}
                      {(u.roles || []).filter(r => NON_STAFF_TYPE_LABELS[r]).map(r => (
                        <Pill key={r} tone="neutral">{NON_STAFF_TYPE_LABELS[r]}</Pill>
                      ))}
                      {extraRoles.length > 0 && (
                        <Pill tone="warn">⚠ 兼具 {extraRoles.join(', ')}</Pill>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '13px 16px' }}>
                    {u.status === 'approved' ? <Pill tone="accent" dot>已核准</Pill>
                      : u.status === 'disabled' ? <Pill tone="neutral">已註銷</Pill>
                      : <Pill tone="warn" dot>{u.status || '—'}</Pill>}
                  </td>
                  <td style={{ padding: '13px 24px 13px 16px', textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 6 }}>
                      <Button size="sm" variant="ghost" onClick={() => setRolesTarget(u)}
                        title="管理此帳號的角色（撤銷 non_staff/analyst 後改回「使用者角色」分頁管理）">角色</Button>
                      {u.status === 'disabled' ? (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setStatusTarget({ user: u, action: 'enable' })}
                            title="恢復帳號，使其可再次登入">恢復帳號</Button>
                          {/* 停用中也可重設密碼 — 免得得先恢復（舊密碼視窗期可登入）才能改密碼 */}
                          <Button size="sm" variant="ghost" onClick={() => setResetTarget(u)}
                            title="產生隨機新密碼並寄到該用戶 email（帳號仍維持註銷，恢復後才可登入）">重設密碼</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => setResetTarget(u)}
                            title="產生隨機新密碼並寄到該用戶 email">重設密碼</Button>
                          <Button size="sm" variant="ghost" onClick={() => setStatusTarget({ user: u, action: 'disable' })}
                            title="註銷帳號，停用後立即無法登入" style={{ color: HR.down }}>註銷帳號</Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
      <div style={{
        marginTop: 14, padding: uiPick('12px 16px', '16px 20px'), background: uiPick(HR.surfaceAlt, GxT.color.card), borderRadius: uiPick(4, GxT.radius.card),
        fontSize: FontSizes.xxs, color: HR.inkSoft, lineHeight: 1.7,
      }}>
        <div style={uiStyle({
          fontSize: FontSizes.trading_lg, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: HR.inkFaint, marginBottom: 6,
        }, { fontSize: GxT.size.label, letterSpacing: 0, textTransform: 'none', fontWeight: GxT.weight.label, color: GxT.color.onV })}>既有員工轉非員工</div>
        ① 到「員工」頁刪除該員工檔（會連動刪除未鎖定期間的薪資單並停用帳號）
        ② 到「使用者角色」分頁先撤銷 employee、再授予 non_staff（授予後帳號改由本頁管理）
        ③ 回本頁把該帳號「恢復帳號」改回啟用。
        若帳號同時兼具其他角色（如 admin），可用列表右側「角色」調整；純管理用 admin 帳號不需要 non_staff 角色。
        調整角色後，該使用者需<strong>重新登入</strong>（或等原 token 過期）權限才會完全生效。
      </div>
      {creating && <CreateAnalystModal onClose={() => setCreating(false)} onCreated={refresh} />}
      {rolesTarget && (
        <UserRolesEditor
          targetUser={rolesTarget}
          roles={roles}
          canEdit={canAssign}
          onClose={() => setRolesTarget(null)}
          onChanged={refresh}
        />
      )}
      {resetTarget && <ResetPasswordConfirm targetUser={resetTarget} onClose={() => setResetTarget(null)} />}
      <ConfirmDialog
        open={!!statusTarget}
        title={statusTarget?.action === 'disable' ? '註銷非員工帳號' : '恢復非員工帳號'}
        message={statusTarget ? (
          statusTarget.action === 'disable'
            ? `確定註銷帳號「${statusTarget.user.username}」？停用後該帳號將立即無法登入，且現有登入工作階段也會失效。可日後在此頁恢復。`
            : `確定恢復帳號「${statusTarget.user.username}」？恢復後該帳號可再次登入。`
        ) : ''}
        danger={statusTarget?.action === 'disable'}
        busy={statusBusy}
        confirmLabel={statusTarget?.action === 'disable' ? '確認註銷' : '確認恢復'}
        onCancel={() => setStatusTarget(null)}
        onConfirm={doSetStatus}
      />
    </>
  );
}

/* ─────────────────────────  Page entry  ─────────────────── */

function RolesPage() {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState('roles');

  React.useEffect(() => {
    api('GET', '/auth/me').then(setUser).catch(() => setUser({})).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <>
        <PageHeader eyebrow="系統 · RBAC" title="角色 & 權限" italic="Roles & Permissions" />
        <SkeletonTable rows={6} />
      </>
    );
  }

  const tabs = [
    { key: 'roles', label: '角色設定', icon: 'settings' },
    { key: 'users', label: '使用者角色', icon: 'file-text' },
    { key: 'analyst', label: '非員工帳號', icon: 'user' },
  ];

  return (
    <>
      <PageHeader eyebrow="系統 · RBAC" title="角色 & 權限" italic="Roles & Permissions" />
      <Tabs tabs={tabs} value={tab} onChange={setTab} />
      {tab === 'roles' && <RolesTab user={user} />}
      {tab === 'users' && <UsersTab user={user} />}
      {tab === 'analyst' && <AnalystAccountsTab user={user} />}
    </>
  );
}

window.RolesPage = RolesPage;
